import { useState } from 'react';
import SectionHeading from './SectionHeading';

const feedbackTypes = ['使用问题', '教程建议', '入口纠错', '功能反馈', '其他说明'];

const guidance = [
  { icon: 'ri-device-line', text: '写清使用的设备与浏览器，便于复现。' },
  { icon: 'ri-route-line', text: '按顺序描述操作步骤和点击位置。' },
  { icon: 'ri-search-eye-line', text: '附上当时搜索的关键词与预期结果。' },
  { icon: 'ri-image-line', text: '重复出现的问题可附截图说明。' },
];

export default function FeedbackPanel() {
  const [type, setType] = useState(feedbackTypes[0]);
  const [content, setContent] = useState('');
  const [contact, setContact] = useState('');
  const [toast, setToast] = useState('');

  const submit = () => {
    if (!content.trim()) {
      setToast('请先填写问题描述再提交');
    } else if (content.length > 500) {
      setToast('描述请控制在 500 字以内');
    } else {
      setToast('演示页面：反馈不会真正提交，可直接用于查看交互效果');
    }
    window.setTimeout(() => setToast(''), 2600);
  };

  return (
    <section id="feedback" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="反馈说明"
          title="遇到问题？这样反馈更容易被处理"
          desc="反馈通道用于收集使用问题与教程建议。写清关键信息，帮助中心定位起来更快，处理也更高效。"
        />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
            <h3 className="font-heading text-lg text-foreground-950">反馈时请尽量说明</h3>
            <ul className="mt-4 space-y-4">
              {guidance.map((g) => (
                <li key={g.text} className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className={`${g.icon} text-lg`}></i>
                  </span>
                  <span className="text-sm text-foreground-700 leading-relaxed pt-1.5">{g.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-background-200 flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                <i className="ri-timer-flash-line text-xl"></i>
              </span>
              <div>
                <div className="font-heading text-lg text-foreground-950">24 小时内</div>
                <div className="text-xs text-foreground-500">一般响应时间</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="反馈类型"
            >
              {feedbackTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`px-3.5 py-2 rounded-full text-xs md:text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                    type === t
                      ? 'bg-secondary-500 text-background-50 border-secondary-500'
                      : 'bg-background-50 text-foreground-700 border-background-200 hover:border-secondary-400'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-foreground-700 mb-2" htmlFor="fb-contact">
                  联系方式（选填）
                </label>
                <input
                  id="fb-contact"
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="邮箱或昵称，方便回复"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>
              <div>
                <label className="block text-sm text-foreground-700 mb-2" htmlFor="fb-type">
                  当前反馈类型
                </label>
                <input
                  id="fb-type"
                  type="text"
                  value={type}
                  readOnly
                  className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-600 outline-none"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm text-foreground-700 mb-2" htmlFor="fb-content">
                问题描述
              </label>
              <textarea
                id="fb-content"
                value={content}
                maxLength={500}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请描述你遇到的情况、操作步骤与期望结果（不超过 500 字）"
                className="w-full min-h-[140px] rounded-md border border-background-200 bg-background-50 p-3 text-sm text-foreground-950 outline-none resize-none focus:border-primary-400 placeholder:text-foreground-400"
              />
              <div className="mt-1 text-right text-xs text-foreground-400">{content.length}/500</div>
            </div>

            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-foreground-500">本页面为演示展示，提交不会真正发送。</p>
              <button
                type="button"
                onClick={submit}
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line"></i>
                提交反馈
              </button>
            </div>

            {toast && (
              <div className="mt-4 px-4 py-3 rounded-md bg-accent-100 border border-accent-200 text-accent-900 text-sm">
                {toast}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}